/**
 * ItemQuickPanel
 *
 * Slide-in right panel for quick-editing any item without leaving the list.
 *
 * Tabs:
 *   Triage   — triage decisions (intake) or status actions (active items)
 *   Details  — title, description, desired outcome, category, altitude, bucket, queue, source
 *   Organize — collection membership (add/remove), tags
 *   Readiness — gap checklist
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Check,
  Clock,
  XCircle,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  Tag,
  Loader2,
  Plus,
  Minus,
  Layers,
} from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemCategoriesStore } from '../../../stores/prioritization/itemCategoriesStore';
import { useTriageStore } from '../../../stores/prioritization/triageStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useCollectionsStore } from '../../../stores/prioritization/collectionsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { fieldsInSpot } from '../../../lib/customFieldPlacement';
import { CustomFieldInput } from './CustomFieldsSection';
import { useItemSourcesStore } from '../../../stores/prioritization/itemSourcesStore';
import { itemSourceLabel } from '../../../lib/itemSourcesService';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { computeItemReadiness } from '../../../lib/itemReadiness';
import { createEmptyQuickChecks } from '../../../types/prioritization/triage';
import type { TriageOutcome } from '../../../types/prioritization/triage';
import type { Item } from '../../../types/prioritization/lifecycle';
import { ALTITUDE_LEVELS_ORDERED } from '../../../types/prioritization/itemLifecycle';
import { useAltitudeText } from '../../../hooks/prioritization/useAltitudeText';
import { CATEGORY_BADGE, CATEGORY_DOT, CATEGORY_BADGE_FALLBACK, CATEGORY_DOT_FALLBACK } from '../../../lib/itemCategoryColors';
import { useItemHref } from '@/lib/itemLinkScope';
import { Select } from '@/components/ds';

// ============================================================================
// Types
// ============================================================================

export type QuickPanelTab = 'triage' | 'details' | 'organize' | 'readiness';

export interface ItemQuickPanelProps {
  itemId: string | null;
  itemIds?: string[];
  onClose: () => void;
  onNavigate?: (itemId: string) => void;
  defaultTab?: QuickPanelTab;
}

// ============================================================================
// Constants
// ============================================================================

// ============================================================================
// Small helpers
// ============================================================================

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-1.5">
      {children}
    </p>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  );
}

const INPUT_CLS =
  'no-dark w-full  border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-2 text-[12.5px] text-[var(--text-sec)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:border-[var(--slate)] dark:focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 dark:focus:ring-[var(--active-tint)] outline-none transition';

// ============================================================================
// Component
// ============================================================================

export function ItemQuickPanel({
  itemId,
  itemIds = [],
  onClose,
  onNavigate,
  defaultTab = 'triage',
}: ItemQuickPanelProps) {
  const itemHref = useItemHref();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<QuickPanelTab>(defaultTab === 'triage' ? 'triage' : defaultTab);
  const [triageNote, setTriageNote] = useState('');
  const [triageSaving, setTriageSaving] = useState(false);
  const [triageDone, setTriageDone] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Inline editing state for title
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const items      = useItemsStore((s) => s.items);
  const updateItem = useItemsStore((s) => s.updateItem);

  const altitudeText  = useAltitudeText();
  const categories    = useItemCategoriesStore((s) => s.categories);
  const fetchCategories = useItemCategoriesStore((s) => s.fetchCategories);

  const submitTriageAssessment = useTriageStore((s) => s.submitTriageAssessment);

  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const allQueues = useMemo(
    () =>
      Array.from(orgUnits.values())
        .filter((ou) => ou.queueSettings?.enabled)
        .map((ou) => ({ id: ou.id, label: ou.displayName ?? ou.name ?? ou.id })),
    [orgUnits]
  );

  const collections         = useCollectionsStore((s) => s.collections);
  const memberships         = useCollectionsStore((s) => s.memberships);
  const addItemToCollection = useCollectionsStore((s) => s.addItemToCollection);
  const removeItemFromCollection = useCollectionsStore((s) => s.removeItemFromCollection);
  const fetchCollections    = useCollectionsStore((s) => s.fetchFromSupabase);

  const item = itemId ? (items.find((i) => i.id === itemId) ?? null) : null;
  const attributes = useAttributesStore((s) => s.attributes);
  const quickFields = useMemo(() => fieldsInSpot(attributes, 'quickPanel'), [attributes]);
  const itemSources = useItemSourcesStore((s) => s.sources);
  const fetchItemSources = useItemSourcesStore((s) => s.fetchSources);
  const activeSources = useMemo(() => itemSources.filter((x) => x.isActive), [itemSources]);
  useEffect(() => {
    const ws = item?.workspace_id ?? getCurrentWorkspaceId();
    if (ws) void fetchItemSources(ws);
  }, [item?.workspace_id, fetchItemSources]);

  // Load categories + collections once
  useEffect(() => {
    const wsId = getCurrentWorkspaceId();
    if (wsId && categories.length === 0) fetchCategories(wsId);
    if (wsId && collections.length === 0) fetchCollections(wsId);
  }, [categories.length, collections.length, fetchCategories, fetchCollections]);

  // Reset state when item changes
  useEffect(() => {
    setTriageNote('');
    setTriageDone(false);
    setSaveStatus('idle');
    setEditingTitle(false);
    if (item) {
      setTitleDraft(item.title ?? '');
      const isIntake = item.lifecycle_stage === 'intake';
      // A caller that opens on a named tab (the readiness list opens on
      // Details, to fix a field) keeps it as it steps from item to item.
      setActiveTab(defaultTab !== 'triage' ? defaultTab : isIntake ? 'triage' : 'details');
    }
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus title input when editing starts
  useEffect(() => {
    if (editingTitle) titleInputRef.current?.focus();
  }, [editingTitle]);

  // Collections the current item belongs to
  const itemCollectionIds = useMemo(
    () => new Set(memberships.filter((m) => m.itemId === itemId).map((m) => m.collectionId)),
    [memberships, itemId]
  );

  // Prev / Next navigation
  const currentIndex = itemIds.indexOf(itemId ?? '');
  const prevId = currentIndex > 0 ? itemIds[currentIndex - 1] : null;
  const nextId = currentIndex < itemIds.length - 1 ? itemIds[currentIndex + 1] : null;

  // Debounced save feedback
  const triggerSave = useCallback(() => {
    setSaveStatus('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setSaveStatus('saved'), 600);
    setTimeout(() => setSaveStatus('idle'), 2200);
  }, []);

  const handleFieldUpdate = useCallback(
    async (field: string, value: unknown) => {
      if (!itemId) return;
      triggerSave();
      await updateItem(itemId, { [field]: value } as Partial<Item>);
    },
    [itemId, updateItem, triggerSave]
  );

  const handleCategoryChange = useCallback(
    async (value: string | null) => {
      if (!itemId) return;
      triggerSave();
      if (!value) {
        await updateItem(itemId, { itemCategory: null, altitudeLevel: null });
      } else {
        await updateItem(itemId, { itemCategory: value });
      }
    },
    [itemId, updateItem, triggerSave]
  );

  const commitTitle = useCallback(async () => {
    if (!itemId || !item) return;
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== item.title) {
      await handleFieldUpdate('title', trimmed);
    }
    setEditingTitle(false);
  }, [itemId, item, titleDraft, handleFieldUpdate]);

  const handleCollectionToggle = useCallback(
    async (collectionId: string) => {
      if (!itemId) return;
      const wsId = getCurrentWorkspaceId();
      if (!wsId) return;
      if (itemCollectionIds.has(collectionId)) {
        await removeItemFromCollection(collectionId, itemId);
      } else {
        await addItemToCollection(collectionId, itemId);
      }
    },
    [itemId, itemCollectionIds, addItemToCollection, removeItemFromCollection]
  );

  const altitudeSelectOptions = useMemo(() => {
    const cat = item?.itemCategory ?? null;
    return ALTITUDE_LEVELS_ORDERED.map((level) => ({
      value: level,
      // The field is labelled Altitude: the number first, the name as a tag.
      label: altitudeText.format(level, cat, { short: true }) ?? String(level),
    }));
  }, [item?.itemCategory, altitudeText]);

  const handleTriage = useCallback(
    async (outcome: TriageOutcome) => {
      if (!itemId || triageSaving) return;
      setTriageSaving(true);
      try {
        await submitTriageAssessment(itemId, {
          itemId,
          quickChecks: createEmptyQuickChecks(),
          outcome,
          notes: triageNote || undefined,
        });
        setTriageDone(true);
        if (nextId && onNavigate) {
          setTimeout(() => onNavigate(nextId), 600);
        }
      } finally {
        setTriageSaving(false);
      }
    },
    [itemId, triageSaving, submitTriageAssessment, triageNote, nextId, onNavigate]
  );

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-8">
        {itemId ? (
          <Loader2 className="h-7 w-7 animate-spin text-[var(--text-ter)]" />
        ) : (
          <>
            <div className="h-12 w-12 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] flex items-center justify-center">
              <Tag className="h-5 w-5 text-[var(--text-ter)]" />
            </div>
            <p className="text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)]">Select an item to review</p>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] max-w-[200px]">Click any row to open the quick panel.</p>
          </>
        )}
      </div>
    );
  }

  const isIntake   = item.lifecycle_stage === 'intake';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const exitStatus = (item as any).exit_status ?? 'active';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const readiness  = computeItemReadiness(item as any);
  const requiredGaps = readiness.required;
  const activeCategories = categories.filter((c) => c.isActive !== false);

  const categoryKey = item.itemCategory ?? '';
  const catBadgeClass = CATEGORY_BADGE[categoryKey] ?? CATEGORY_BADGE_FALLBACK;
  const catDotClass   = CATEGORY_DOT[categoryKey]   ?? CATEGORY_DOT_FALLBACK;

  const TABS: Array<{ key: QuickPanelTab; label: string }> = [
    { key: 'triage',    label: isIntake ? 'Triage' : 'Status' },
    { key: 'details',   label: 'Details' },
    { key: 'organize',  label: 'Organize' },
    { key: 'readiness', label: 'Readiness' },
  ];

  return (
    <div className="flex flex-col h-full bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">

      {/* ── Panel header ── */}
      <div className="px-4 pt-4 pb-3 border-b border-[var(--rule)] dark:border-[var(--rule)] flex-shrink-0">
        <div className="flex items-start gap-2 mb-2">
          {/* Editable title */}
          <div className="flex-1 min-w-0">
            {editingTitle ? (
              <input
                ref={titleInputRef}
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTitle();
                  if (e.key === 'Escape') { setTitleDraft(item.title ?? ''); setEditingTitle(false); }
                }}
                className="w-full text-sm font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug bg-transparent border-b-2 border-[var(--slate)] outline-none"
              />
            ) : (
              <button
                onClick={() => { setTitleDraft(item.title ?? ''); setEditingTitle(true); }}
                className="w-full text-left text-sm font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
                title="Click to edit title"
              >
                {item.title}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => navigate(itemHref(item.id))}
              title="Open full page"
              className="p-1 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--active-tint)] transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onClose}
              className="p-1 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Status pills row */}
        <div className="flex items-center gap-2 flex-wrap">
          {isIntake ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)]" />
              Intake
            </span>
          ) : exitStatus === 'deferred' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)]" />
              Deferred
            </span>
          ) : exitStatus === 'archived' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--hover-tint)] text-[var(--text-ter)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--text-ter)]" />
              Archived
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)]" />
              Active
            </span>
          )}
          {item.itemCategory && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${catBadgeClass.bg} ${catBadgeClass.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${catDotClass}`} />
              {item.itemCategory}
            </span>
          )}
          {itemCollectionIds.size > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
              <Layers className="h-2.5 w-2.5" />
              {itemCollectionIds.size} collection{itemCollectionIds.size !== 1 ? 's' : ''}
            </span>
          )}
          {saveStatus !== 'idle' && (
            <span className={`text-[11px] ml-auto ${saveStatus === 'saved' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
              {saveStatus === 'saving' ? 'Saving…' : '✓ Saved'}
            </span>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-[var(--rule)] dark:border-[var(--rule)] flex-shrink-0">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 text-[11px] font-medium transition-colors border-b-2 ${
              activeTab === tab.key
                ? 'text-[var(--text-ter)] border-[var(--slate)]'
                : 'text-[var(--text-ter)] dark:text-[var(--text-dim)] border-transparent hover:text-[var(--text-sec)]'
            }`}
          >
            {tab.key === 'triage' && isIntake && requiredGaps.length > 0 ? (
              <span className="flex items-center justify-center gap-1">
                {tab.label}
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--coral)]" />
              </span>
            ) : tab.key === 'details' && !item.itemCategory ? (
              <span className="flex items-center justify-center gap-1">
                {tab.label}
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)]" />
              </span>
            ) : (
              tab.label
            )}
          </button>
        ))}
      </div>

      {/* ── Tab body ── */}
      <div className="flex-1 overflow-y-auto">

        {/* ─── Triage / Status tab ─── */}
        {activeTab === 'triage' && (
          <div className="p-4 space-y-4">

            {item.description && (
              <div className="bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-3">
                <p className="text-[12.5px] text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
                  {item.description}
                </p>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-[var(--text-ter)]">
                  {item.createdBy && <span>by {item.createdBy}</span>}
                  {item.createdAt && (
                    <span>{Math.floor((Date.now() - new Date(item.createdAt).getTime()) / 86400000)}d ago</span>
                  )}
                </div>
              </div>
            )}

            {/* Intake decision grid */}
            {isIntake && !triageDone && (
              <div>
                <SectionLabel>Triage Decision</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { outcome: 'pursue' as TriageOutcome, label: 'Pursue', desc: 'Moves to active backlog', color: 'green', icon: <Check className="h-3.5 w-3.5 text-[var(--green)]" /> },
                    { outcome: 'defer' as TriageOutcome, label: 'Defer', desc: 'Hold for later', color: 'amber', icon: <Clock className="h-3.5 w-3.5 text-[var(--mustard)]" /> },
                    { outcome: 'reject' as TriageOutcome, label: 'Reject', desc: 'Out of scope', color: 'red', icon: <XCircle className="h-3.5 w-3.5 text-[var(--coral)]" /> },
                    { outcome: 'needs_clarification' as TriageOutcome, label: 'Needs Info', desc: 'Request clarification', color: 'blue', icon: <HelpCircle className="h-3.5 w-3.5 text-[var(--text-ter)]" /> },
                  ].map(({ outcome, label, desc, color, icon }) => (
                    <button
                      key={outcome}
                      onClick={() => handleTriage(outcome)}
                      disabled={triageSaving}
                      className={`group flex flex-col gap-1.5  border-2 border-${color}-200 bg-${color}-50 px-3 py-3 text-left transition hover:border-${color}-400 hover:bg-${color}-100 disabled:opacity-50`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className={`h-6 w-6 rounded-full bg-${color}-100 group-hover:bg-${color}-200 flex items-center justify-center transition`}>
                          {icon}
                        </div>
                        <span className={`text-[12px] font-semibold text-${color}-800`}>{label}</span>
                      </div>
                      <p className={`text-[11px] text-${color}-700 leading-snug`}>{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {triageDone && (
              <div className="flex items-center gap-2 bg-[var(--green-dim)] border border-[var(--green)] px-3 py-3">
                <CheckCircle className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
                <span className="text-[12.5px] text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                  Decision saved{nextId ? ' — advancing to next item' : ''}
                </span>
              </div>
            )}

            {/* Non-intake: status actions */}
            {!isIntake && (
              <div>
                <SectionLabel>Status Actions</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {exitStatus !== 'active' && (
                    <button onClick={() => handleFieldUpdate('exit_status', 'active')} className="flex items-center gap-1.5 px-3 py-2 border border-[var(--green)] bg-[var(--green-dim)] text-[12px] font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] transition">
                      <Check className="h-3.5 w-3.5" /> Activate
                    </button>
                  )}
                  {exitStatus !== 'deferred' && (
                    <button onClick={() => handleFieldUpdate('exit_status', 'deferred')} className="flex items-center gap-1.5 px-3 py-2 border border-[var(--mustard)] bg-[var(--mustard-dim)] text-[12px] font-medium text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition">
                      <Clock className="h-3.5 w-3.5" /> Defer
                    </button>
                  )}
                  {exitStatus !== 'archived' && (
                    <button onClick={() => handleFieldUpdate('exit_status', 'archived')} className="flex items-center gap-1.5 px-3 py-2 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface)] text-[12px] font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition">
                      <XCircle className="h-3.5 w-3.5" /> Archive
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Triage note */}
            {isIntake && !triageDone && (
              <div>
                <SectionLabel>Triage Note (optional)</SectionLabel>
                <textarea
                  value={triageNote}
                  onChange={(e) => setTriageNote(e.target.value)}
                  rows={2}
                  placeholder="Add context for your decision…"
                  className={INPUT_CLS + ' resize-none'}
                />
              </div>
            )}
          </div>
        )}

        {/* ─── Details tab ─── */}
        {activeTab === 'details' && (
          <div className="p-4 space-y-4">

            {/* Description */}
            <FieldRow label="Description">
              <textarea
                value={item.description ?? ''}
                onChange={(e) => handleFieldUpdate('description', e.target.value || null)}
                rows={3}
                placeholder="What is this item about?"
                className={INPUT_CLS + ' resize-none'}
              />
            </FieldRow>

            {/* Category */}
            <FieldRow label="Category">
              <Select
                value={item.itemCategory ?? ''}
                onChange={(e) => void handleCategoryChange(e.target.value || null)}
                className={`${INPUT_CLS} ${!item.itemCategory ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/20' : ''}`}
              >
                <option value="">— Select category —</option>
                {(activeCategories.length > 0 ? activeCategories : [
                  { id: 'problem', slug: 'problem', label: 'Problem' },
                  { id: 'opportunity', slug: 'opportunity', label: 'Opportunity' },
                  { id: 'risk', slug: 'risk', label: 'Risk' },
                  { id: 'solution', slug: 'solution', label: 'Solution' },
                  { id: 'commitment', slug: 'commitment', label: 'Commitment' },
                  { id: 'investment', slug: 'investment', label: 'Investment' },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ] as any[]).map((c: any) => (
                  <option key={c.id} value={c.slug}>{c.label}</option>
                ))}
              </Select>
              {!item.itemCategory && (
                <p className="mt-1 text-[11px] text-[var(--mustard)]">Required for scoring and ranking</p>
              )}
            </FieldRow>

            {/* Altitude */}
            <FieldRow label="Altitude">
              <Select
                disabled={!item.itemCategory}
                value={item.itemCategory ? (item.altitudeLevel ?? '') : ''}
                onChange={(e) => handleFieldUpdate('altitudeLevel', e.target.value ? Number(e.target.value) : null)}
                className={`${INPUT_CLS} ${!item.itemCategory ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <option value="">{!item.itemCategory ? '— Select category first —' : '— Not set —'}</option>
                {altitudeSelectOptions.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </Select>
            </FieldRow>

            {/* Queue — available for all items */}
            {allQueues.length > 0 && (
              <FieldRow label="Queue">
                <Select
                  value={item.queueId ?? ''}
                  onChange={(e) => handleFieldUpdate('queueId', e.target.value || null)}
                  className={INPUT_CLS}
                >
                  <option value="">General</option>
                  {allQueues.map((q) => (
                    <option key={q.id} value={q.id}>{q.label}</option>
                  ))}
                </Select>
              </FieldRow>
            )}

            {/* Idea source — from the workspace list (Settings → Idea Sources) */}
            <FieldRow label="Idea source">
              <Select
                value={item.source ?? ''}
                onChange={(e) => handleFieldUpdate('source', e.target.value || null)}
                className={INPUT_CLS}
              >
                <option value="">Not set</option>
                {activeSources.filter((x) => x.kind === 'idea').map((x) => (
                  <option key={x.id} value={x.value}>{x.label}</option>
                ))}
                {activeSources.some((x) => x.kind === 'arrival') && (
                  <optgroup label="How it arrived">
                    {activeSources.filter((x) => x.kind === 'arrival').map((x) => (
                      <option key={x.id} value={x.value}>{x.label}</option>
                    ))}
                  </optgroup>
                )}
                {item.source && !activeSources.some((x) => x.value === item.source) && (
                  <option value={item.source}>{itemSourceLabel(item.source, itemSources)} (not in the list)</option>
                )}
              </Select>
            </FieldRow>

            {/* Custom fields placed in the quick panel (Settings → Custom Fields → Shows on). */}
            {quickFields.map((field) => (
              <FieldRow key={field.key} label={field.label}>
                <CustomFieldInput
                  field={field}
                  metadata={item.metadata as Record<string, unknown> | undefined}
                  onSave={(u) => void handleFieldUpdate('metadata', u.metadata)}
                />
              </FieldRow>
            ))}

          </div>
        )}

        {/* ─── Organize tab ─── */}
        {activeTab === 'organize' && (
          <div className="p-4 space-y-5">

            {/* Collections */}
            <div>
              <SectionLabel>Collections</SectionLabel>
              {collections.length === 0 ? (
                <p className="text-[12px] text-[var(--text-ter)] italic">No collections yet — create one from the Collections board.</p>
              ) : (
                <div className="space-y-1.5">
                  {collections.map((col) => {
                    const isMember = itemCollectionIds.has(col.id);
                    return (
                      <button
                        key={col.id}
                        onClick={() => handleCollectionToggle(col.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5  border text-left transition-all ${
                          isMember
                            ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:border-[var(--slate)]/50 dark:bg-[var(--slate)]/30'
                            : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
                        }`}
                      >
                        <div className={`flex-shrink-0 w-5 h-5  border flex items-center justify-center transition-all ${
                          isMember
                            ? 'bg-[var(--slate)] border-[var(--slate)]'
                            : 'border-[var(--rule)] dark:border-[var(--rule-bright)]'
                        }`}>
                          {isMember && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[12.5px] font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                            {col.name}
                          </p>
                          {col.description && (
                            <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)] truncate">{col.description}</p>
                          )}
                        </div>
                        {isMember ? (
                          <Minus className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                        ) : (
                          <Plus className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <SectionLabel>Tags</SectionLabel>
              <input
                type="text"
                value={item.tags?.join(', ') ?? ''}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map((t) => t.trim()).filter(Boolean);
                  handleFieldUpdate('tags', tags);
                }}
                placeholder="comma-separated tags…"
                className={INPUT_CLS}
              />
              {(item.tags?.length ?? 0) > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {item.tags!.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[11px] text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {tag}
                      <button
                        onClick={() => handleFieldUpdate('tags', item.tags!.filter((t) => t !== tag))}
                        className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Provenance */}
            <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] pt-3 space-y-1.5">
              {item.createdBy && (
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-ter)] w-16 flex-shrink-0">Submitted</span>
                  <span className="text-[12px] text-[var(--text-sec)] dark:text-[var(--text-sec)] truncate">{item.createdBy}</span>
                </div>
              )}
              {item.createdAt && (
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-ter)] w-16 flex-shrink-0">Created</span>
                  <span className="text-[12px] text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    {new Date(item.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}
              {item.updatedAt && (
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-ter)] w-16 flex-shrink-0">Updated</span>
                  <span className="text-[12px] text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    {new Date(item.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ─── Readiness tab ─── */}
        {activeTab === 'readiness' && (
          <div className="p-4 space-y-4">

            {(() => {
              const total = readiness.all.length + 1;
              const passed = total - readiness.all.length;
              const pct = Math.round((passed / total) * 100);
              const color = pct >= 70 ? 'bg-[var(--green)]' : pct >= 40 ? 'bg-[var(--mustard)]' : 'bg-[var(--coral)]';
              const textColor = pct >= 70 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : pct >= 40 ? 'text-[var(--mustard)]' : 'text-[var(--coral)]';
              return (
                <div className="bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] p-3">
                  <div className="flex items-center justify-between text-[11px] mb-2">
                    <span className="font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">Readiness</span>
                    <span className={`font-bold ${textColor}`}>{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-[var(--hover-tint)] dark:bg-[var(--rule-bright)] rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })()}

            {requiredGaps.length > 0 && (
              <div>
                <SectionLabel>Required ({requiredGaps.length})</SectionLabel>
                <div className="space-y-1.5">
                  {requiredGaps.map((issue, i) => (
                    <div key={`${issue.code}-${i}`} className="flex items-start gap-2">
                      <AlertCircle className="h-3.5 w-3.5 text-[var(--coral)] mt-0.5 flex-shrink-0" />
                      <span className="text-[12px] text-[var(--text-sec)] dark:text-[var(--text-sec)]">{issue.displayText}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {readiness.recommended.length > 0 && (
              <div>
                <SectionLabel>Recommended</SectionLabel>
                <div className="space-y-1.5">
                  {readiness.recommended.map((issue, i) => (
                    <div key={`${issue.code}-${i}`} className="flex items-start gap-2">
                      <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                      <span className="text-[12px] text-[var(--text-sec)] dark:text-[var(--text-ter)]">{issue.displayText}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {readiness.isReady && readiness.recommended.length === 0 && (
              <div className="flex items-center gap-2 bg-[var(--green-dim)] border border-[var(--green)] px-3 py-3">
                <CheckCircle className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
                <span className="text-[12.5px] text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                  All required fields complete — ready for ranking
                </span>
              </div>
            )}

            {requiredGaps.length > 0 && (() => {
              // Map the first required gap's fixTarget to the relevant item detail tab
              const first = requiredGaps[0];
              const tabMap: Record<string, string> = {
                'item-section-classification': 'classification',
                'item-section-values':         'values',
                'item-section-relationships':  'relationships',
                'item-section-clarifications': 'classification',
                'item-top':                    'classification',
              };
              const tab = first.fixTarget ? (tabMap[first.fixTarget] ?? 'classification') : 'classification';
              return (
                <button
                  onClick={() => navigate(itemHref(item.id, tab))}
                  className="w-full text-center text-[12px] text-[var(--text-sec)] hover:text-[var(--text-pri)] py-2 border border-[var(--slate)] bg-[var(--slate-dim)] font-medium transition"
                >
                  Fix "{first.displayText}" on item page →
                </button>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-t border-[var(--rule)] dark:border-[var(--rule)] flex-shrink-0">
        <button
          onClick={() => prevId && onNavigate?.(prevId)}
          disabled={!prevId}
          title="Previous item"
          className="flex items-center gap-1 px-2.5 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] text-[12px] text-[var(--text-ter)] hover:bg-[var(--surface)] disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Prev
        </button>
        <button
          onClick={() => nextId && onNavigate?.(nextId)}
          disabled={!nextId}
          title="Next item"
          className="flex items-center gap-1 px-2.5 py-1.5 border border-[var(--rule)] dark:border-[var(--rule)] text-[12px] text-[var(--text-ter)] hover:bg-[var(--surface)] disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          Next
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <div className="flex-1" />
        <span className="text-[11px] text-[var(--text-ter)] tabular-nums">
          {currentIndex >= 0 ? `${currentIndex + 1} / ${itemIds.length}` : ''}
        </span>
        <button
          onClick={() => navigate(itemHref(item.id))}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate)] text-[12px] font-medium text-white hover:bg-[var(--slate)] transition"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Full page
        </button>
      </div>
    </div>
  );
}
